/**
 * Layout 3: Lodge Bulletin Board
 * Warm/Rustic aesthetic inspired by physical bulletin boards
 * - Cork texture background, papers pinned at angles
 * - Colorful pushpins, layered/overlapping cards
 * - Handwritten accents for warmth, tactile feel
 */
import { useCallback, useMemo, useRef, useState } from "react";
import { NavigationControl } from "react-map-gl/mapbox";
import type { Map as MapboxMap } from "mapbox-gl";
import {
  type PublishedLandingPage,
  type PlaceFeature,
  type PlaceGeoJSON,
  PLACE_TYPE_META,
  PlaceType,
  getPlaceTrack,
} from "../../lib/api";
import { FIT_BOUNDS_PADDING, FLY_TO_OPTIONS, FLY_TO_ZOOM } from "../../lib/mapConstants";
import { routes } from "../../lib/routes";
import { useShare } from "../../hooks/useShare";
import { NomaMap, AdventureMapPlacesLayer, SelectedPlaceLayer, RouteTrackLayer } from "../../components/map";

interface LodgeBulletinLandingProps {
  mapData: PublishedLandingPage;
  places: PlaceGeoJSON;
}

// Deterministic rotation based on index
const getRotation = (idx: number) => {
  const rotations = [-2.5, 1.5, -1, 2, -1.5, 1, -2, 0.5];
  return rotations[idx % rotations.length];
};

// Pushpin component
function Pushpin({ color, className = "" }: { color: string; className?: string }) {
  return (
    <div className={`absolute z-20 ${className}`}>
      {/* Pin head */}
      <div
        className="w-4 h-4 rounded-full relative"
        style={{
          background: `radial-gradient(circle at 35% 35%, ${color}, ${color}dd)`,
          boxShadow: `0 2px 4px rgba(0,0,0,0.3), inset 0 1px 2px rgba(255,255,255,0.3)`,
        }}
      />
      {/* Pin shadow on paper */}
      <div
        className="absolute top-3 left-1/2 w-2 h-2 rounded-full -translate-x-1/2"
        style={{ background: "rgba(0,0,0,0.15)", filter: "blur(2px)" }}
      />
    </div>
  );
}

export default function LodgeBulletinLanding({ mapData, places }: LodgeBulletinLandingProps) {
  const share = useShare();
  const mapSectionRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<PlaceFeature | null>(null);

  const placesByType = useMemo(() => {
    const grouped = new Map<PlaceType, PlaceFeature[]>();
    places.features.forEach((place) => {
      const type = place.properties.type;
      if (!grouped.has(type)) grouped.set(type, []);
      grouped.get(type)!.push(place);
    });
    return grouped;
  }, [places.features]);

  const handleMapLoad = useCallback((map: MapboxMap) => {
    mapRef.current = map;
  }, []);

  const handlePlaceSelect = useCallback(
    (placeId: string) => {
      const place = places.features.find((f) => f.id === placeId);
      if (!place) return;
      setSelectedPlace(place);
      mapRef.current?.flyTo({
        ...FLY_TO_OPTIONS,
        center: place.geometry.coordinates as [number, number],
        zoom: FLY_TO_ZOOM,
      });
      if (mapSectionRef.current) {
        const top = mapSectionRef.current.getBoundingClientRect().top + window.scrollY - 20;
        window.scrollTo({ top, behavior: "smooth" });
      }
    },
    [places.features],
  );

  const handlePlaceClick = useCallback(
    (feature: PlaceFeature) => {
      handlePlaceSelect(feature.id as string);
    },
    [handlePlaceSelect],
  );

  const handleShare = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (mapData.slug) {
        share({
          url: routes.to.landingPages.bySlug(mapData.slug),
          title: mapData.name,
        });
      }
    },
    [share, mapData.slug, mapData.name],
  );

  const colors = {
    terracotta: "#c75d45",
    sage: "#5b8a72",
    wheat: "#d4a84b",
    plum: "#8a7ba8",
    slate: "#5a7a8a",
    cork: "#c4a574",
    corkDark: "#a68b5b",
    paper: "#fffef8",
    paperAlt: "#faf5e8",
  };

  const pinColors = [colors.terracotta, colors.sage, colors.wheat, colors.plum, colors.slate];

  // Cork board - lighter warm tan like natural cork
  const corkNoiseUri = `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='turbulence' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`;

  return (
    <div
      className="min-h-screen relative"
      style={{
        fontFamily: "'Source Sans 3', 'Segoe UI', sans-serif",
        background: "linear-gradient(170deg, #c9a880 0%, #c2a070 50%, #ba9768 100%)",
      }}
    >
      {/* Cork texture overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: corkNoiseUri,
          backgroundRepeat: "repeat",
          opacity: 0.35,
          mixBlendMode: "overlay",
        }}
      />
      {/* Wooden frame top edge */}
      <div
        className="h-3"
        style={{
          background: "linear-gradient(180deg, #5a4a3a 0%, #8b7355 50%, #6b5a48 100%)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
        }}
      />

      {/* Hero Section */}
      <div ref={mapSectionRef} className="px-4 md:px-8 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Two column hero: Title card + Map card */}
          <div className="grid md:grid-cols-5 gap-6 md:gap-8 items-start">
            {/* Title card - pinned note (smaller) */}
            <div
              className="relative p-5 md:p-6 md:col-span-2"
              style={{
                background: colors.paper,
                transform: "rotate(-1deg)",
                boxShadow: "4px 4px 12px rgba(0,0,0,0.15), 0 0 1px rgba(0,0,0,0.1)",
              }}
            >
              <Pushpin color={colors.terracotta} className="-top-2 left-6" />
              <Pushpin color={colors.sage} className="-top-2 right-10" />

              <p
                className="text-lg md:text-xl mb-1"
                style={{
                  fontFamily: "'Caveat', cursive",
                  color: colors.terracotta,
                }}
              >
                Welcome to
              </p>
              <h1
                className="text-2xl md:text-3xl font-semibold leading-tight mb-3"
                style={{
                  color: "#2d2a26",
                  fontFamily: "'Fraunces', Georgia, serif",
                }}
              >
                {mapData.name}
              </h1>
              {mapData.description && (
                <p className="text-sm leading-relaxed mb-4" style={{ color: "#5a554e" }}>
                  {mapData.description}
                </p>
              )}
              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all hover:scale-105"
                style={{ background: colors.terracotta, color: "#fff" }}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M4 12V14H12V12M8 2V10M8 2L5 5M8 2L11 5" />
                </svg>
                Share
              </button>

              {/* Decorative tape */}
              <div
                className="absolute -bottom-2 right-4 w-14 h-4 opacity-60"
                style={{
                  background: "linear-gradient(90deg, transparent, #e8dcc8 10%, #e8dcc8 90%, transparent)",
                  transform: "rotate(5deg)",
                }}
              />
            </div>

            {/* Map card - pinned (bigger) */}
            <div
              className="relative md:col-span-3"
              style={{
                transform: "rotate(1deg)",
              }}
            >
              <Pushpin color={colors.wheat} className="-top-2 left-6" />
              <Pushpin color={colors.plum} className="-top-2 right-8" />

              <div
                className="p-2 pb-4"
                style={{
                  background: colors.paper,
                  boxShadow: "4px 4px 16px rgba(0,0,0,0.2), 0 0 1px rgba(0,0,0,0.1)",
                }}
              >
                <div className="h-72 md:h-96 w-full">
                  <NomaMap bounds={places} padding={FIT_BOUNDS_PADDING.desktop} onLoad={handleMapLoad}>
                    <NavigationControl position="bottom-right" />
                    <AdventureMapPlacesLayer places={places} onClick={handlePlaceClick} />
                    <RouteTrackLayer track={selectedPlace ? getPlaceTrack(selectedPlace.properties) : undefined} />
                    <SelectedPlaceLayer place={selectedPlace} />
                  </NomaMap>
                </div>
                <p
                  className="text-center mt-3 text-base"
                  style={{
                    fontFamily: "'Caveat', cursive",
                    color: "#7a6b5a",
                  }}
                >
                  Click a pin to explore!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Selected place popup - floating pinned card */}
      {selectedPlace && (
        <div
          className="fixed top-24 right-4 md:right-8 w-72 md:w-80 z-50 animate-in slide-in-from-right-4 duration-300"
          style={{ transform: "rotate(2deg)" }}
        >
          <div
            className="relative p-5"
            style={{
              background: colors.paper,
              boxShadow: "4px 6px 20px rgba(0,0,0,0.25)",
            }}
          >
            <Pushpin color={colors.sage} className="-top-2 left-1/2 -translate-x-1/2" />
            <button
              onClick={() => setSelectedPlace(null)}
              className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center transition-colors hover:bg-amber-100"
              style={{ color: "#7a6b5a" }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" stroke="currentColor" strokeWidth="2">
                <path d="M3 3l8 8M11 3L3 11" />
              </svg>
            </button>
            <p className="text-xs uppercase tracking-wider mb-1" style={{ color: "#7a6b5a" }}>
              {PLACE_TYPE_META[selectedPlace.properties.type].label}
            </p>
            <h3
              className="text-xl font-semibold mb-2"
              style={{
                color: "#2d2a26",
                fontFamily: "'Fraunces', Georgia, serif",
              }}
            >
              {selectedPlace.properties.name}
            </h3>
            {selectedPlace.properties.description && (
              <p
                className="text-base leading-relaxed"
                style={{
                  fontFamily: "'Caveat', cursive",
                  color: "#5a554e",
                }}
              >
                &ldquo;{selectedPlace.properties.description}&rdquo;
              </p>
            )}
          </div>
        </div>
      )}

      {/* Places sections */}
      <main className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-12">
        {places.features.length === 0 ? (
          <div
            className="text-center py-16"
            style={{
              fontFamily: "'Caveat', cursive",
              color: "#fffef8",
              fontSize: "1.5rem",
              textShadow: "1px 1px 2px rgba(0,0,0,0.2)",
            }}
          >
            We&apos;re still gathering our favorite spots...
          </div>
        ) : (
          <div className="space-y-12">
            {Array.from(placesByType.entries()).map(([type, typePlaces], typeIdx) => (
              <section key={type}>
                {/* Section header - handwritten label */}
                <div
                  className="mb-6 inline-block px-4 py-2 rounded"
                  style={{
                    background: "rgba(255,254,248,0.85)",
                    transform: `rotate(${getRotation(typeIdx) * 0.3}deg)`,
                    boxShadow: "2px 2px 8px rgba(0,0,0,0.1)",
                  }}
                >
                  <h2
                    className="text-2xl"
                    style={{
                      fontFamily: "'Caveat', cursive",
                      color: pinColors[typeIdx % pinColors.length],
                    }}
                  >
                    {PLACE_TYPE_META[type].labelPlural}
                    <span className="ml-2 text-lg" style={{ color: "#9a958e" }}>
                      ({typePlaces.length})
                    </span>
                  </h2>
                </div>

                {/* Places grid - overlapping pinned cards */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {typePlaces.map((place, idx) => {
                    const rotation = getRotation(typeIdx * 10 + idx);
                    const pinColor = pinColors[(typeIdx + idx) % pinColors.length];
                    const isSelected = selectedPlace?.id === place.id;

                    return (
                      <button
                        key={place.id}
                        onClick={() => handlePlaceSelect(place.id as string)}
                        className="relative text-left group"
                        style={{
                          transform: `rotate(${rotation}deg)`,
                          transition: "transform 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "rotate(0deg) scale(1.02)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = `rotate(${rotation}deg)`;
                        }}
                      >
                        <Pushpin color={pinColor} className="-top-2 left-1/2 -translate-x-1/2" />

                        <div
                          className="p-5 min-h-32"
                          style={{
                            background: isSelected ? colors.paperAlt : colors.paper,
                            boxShadow: isSelected ? "4px 6px 16px rgba(0,0,0,0.2)" : "3px 4px 10px rgba(0,0,0,0.12)",
                          }}
                        >
                          <h3 className="text-lg font-medium mb-2" style={{ color: "#2d2a26" }}>
                            {place.properties.name}
                          </h3>
                          {place.properties.description && (
                            <p
                              className="text-base leading-relaxed line-clamp-3"
                              style={{
                                fontFamily: "'Caveat', cursive",
                                color: "#6a655e",
                              }}
                            >
                              {place.properties.description}
                            </p>
                          )}
                          <div
                            className="mt-3 text-sm flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                            style={{ color: pinColor }}
                          >
                            <span>View on map</span>
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 14 14"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                            >
                              <path d="M3 7h8M8 4l3 3-3 3" />
                            </svg>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>

      {/* Footer - pinned note */}
      <footer className="px-4 md:px-8 py-12">
        <div className="max-w-xl mx-auto">
          <div
            className="relative p-8 text-center"
            style={{
              background: colors.paper,
              transform: "rotate(-0.5deg)",
              boxShadow: "4px 4px 16px rgba(0,0,0,0.15)",
            }}
          >
            <Pushpin color={colors.terracotta} className="-top-2 left-1/2 -translate-x-1/2" />

            <p
              className="text-2xl mb-2"
              style={{
                fontFamily: "'Caveat', cursive",
                color: colors.terracotta,
              }}
            >
              Enjoyed this guide?
            </p>
            <p className="text-base mb-6" style={{ color: "#5a554e" }}>
              Create your own adventure map and share your favorite places with friends and guests.
            </p>
            <a
              href={routes.to.home()}
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full text-base font-medium transition-all hover:scale-105"
              style={{ background: colors.terracotta, color: "#fff" }}
            >
              Create your own map
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 8h10M9 4l4 4-4 4" />
              </svg>
            </a>
            <div className="mt-8 pt-4 border-t" style={{ borderColor: "#e5e0d8" }}>
              <p className="text-sm mb-2" style={{ color: "#9a958e" }}>
                Curated with care by {mapData.curatedBy?.displayName ?? "your local adventurers"}
              </p>
              <p className="text-sm" style={{ color: "#9a958e" }}>
                Made with{" "}
                <a href={routes.to.home()} className="font-medium hover:underline" style={{ color: colors.sage }}>
                  noma.to
                </a>
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Wooden frame bottom edge */}
      <div
        className="h-3"
        style={{
          background: "linear-gradient(0deg, #5a4a3a 0%, #8b7355 50%, #6b5a48 100%)",
          boxShadow: "0 -2px 8px rgba(0,0,0,0.3)",
        }}
      />
    </div>
  );
}
